import { BffApiClientMolecule } from "@/app/api-client/store"
import { getTrendColor } from "@/app/charts/get-trend-color"
import { CollectionFloorPriceChangePeriod } from "@anaconda/core/nft-collection/collection/types"
import { action, atom, computed } from "@reatom/core"
import { molecule } from "bunshi"
import { CollectionMoleculeReatom } from "../store/index.reatom"
import { getApproximateDateLabelWidth, getDateFormatter } from "./date-utils"

const openedAtom = atom(false, "chartOpened")

const periodAtom = atom<CollectionFloorPriceChangePeriod>(
  CollectionFloorPriceChangePeriod.MONTH,
  "chartPeriod",
)

const setPeriod = action((value: string) => {
  const prevPeriod = periodAtom()

  if (isValidPeriod(value) && value !== prevPeriod) {
    periodAtom.set(value)
  }
}, "setPeriod")

export const FloorPriceChartMoleculeReatom = molecule((mol) => {
  const { collectionId } = mol(CollectionMoleculeReatom)
  const bffApiClient = mol(BffApiClientMolecule)

  const activeDot = atom<{ value: number; date: string } | undefined>(
    undefined,
    "chartActiveDot",
  )

  // Return query params (not wrapped query) - TanStack Query used in components
  const floorPriceChangeQueryParams = computed(() => {
    const period = periodAtom()

    return {
      queryKey: ["collection", "floorPriceChange", { collectionId, period }],
      queryFn: async () => {
        const response = await bffApiClient.api.collections[":id"][
          "floor-price-change"
        ].$get({
          param: { id: collectionId },
          query: { period },
        })

        if (!response.ok) throw await response.json()

        return response.json()
      },
    }
  }, "floorPriceChangeQueryParams")

  const dateFormatter = computed(() => {
    const period = periodAtom()
    return getDateFormatter(period)
  }, "dateFormatter")

  const toggleChart = action(() => {
    openedAtom.set(!openedAtom())
  }, "toggleChart")

  const approximateDateLabelWidth = computed(() => {
    const period = periodAtom()
    return getApproximateDateLabelWidth(period)
  }, "approximateDateLabelWidth")

  return {
    // State atoms
    openedAtom,
    periodAtom,
    setPeriod,
    activeDot,

    // Query params
    floorPriceChangeQueryParams,

    // Computed
    dateFormatter,
    toggleChart,
    approximateDateLabelWidth,
  }
})

const isValidPeriod = (
  value: string,
): value is CollectionFloorPriceChangePeriod => {
  return Object.values(CollectionFloorPriceChangePeriod).includes(
    value as CollectionFloorPriceChangePeriod,
  )
}

interface DataPoint {
  date: string
  value: number | null
}

/**
 * In case of new collections, there might be a period of time where it was no floor price (no listed items)
 * This function cuts the beginning of the array until the first non-null price is found
 */
export const cutNullPricesInBeginning = (priceChange: DataPoint[]) => {
  const nonNullPriceIndex = priceChange.findIndex((p) => p.value !== null)

  if (nonNullPriceIndex === -1) return priceChange

  return priceChange.slice(nonNullPriceIndex)
}

/**
 * Utility to extract chart data from query response
 */
export const getChartData = (
  data: { priceChange: DataPoint[] } | undefined,
) => {
  return data ? cutNullPricesInBeginning(data.priceChange) : []
}

/**
 * Utility to extract prices from query response
 */
export const getPrices = (data: { priceChange: DataPoint[] } | undefined) => {
  if (!data) return []

  return data.priceChange.reduce<number[]>(
    (acc, p) => (p.value !== null ? acc.concat(p.value) : acc),
    [],
  )
}

/**
 * Utility to check if there's enough data to show chart
 */
export const hasEnoughDataToShowChart = (prices: number[]) => {
  const nonNullPrices = prices.filter((p) => p !== null)
  return nonNullPrices.length > 1
}

/**
 * Utility to get floor price (latest price)
 */
export const getFloorPrice = (prices: number[]) => {
  return prices[prices.length - 1]
}

/**
 * Utility to get sorted prices
 */
export const getSortedPrices = (prices: number[]) => {
  return [...prices].sort((a, b) => a - b)
}

/**
 * Utility to get max price
 */
export const getMaxPrice = (sortedPrices: number[]) => {
  return sortedPrices[sortedPrices.length - 1]
}

/**
 * Utility to get min price
 */
export const getMinPrice = (sortedPrices: number[]) => {
  return sortedPrices[0]
}

/**
 * Utility to get trend color
 */
export const getTrendColorFromPrices = (prices: number[]) => {
  return getTrendColor(prices)
}
